/* eslint-disable max-len */
import { shade } from 'polished';
import styled, { css } from 'styled-components';

interface LabelProps {
  widthContainerDesktop?: string;
}
const reducedOnePercent = (width: string) => {
  return `calc(${width} - 1%)`;
};

export const Label = styled.div<LabelProps>`
  ${(props) =>
    props.hidden &&
    css`
      display: none;
    `}
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: 8px;

  ${(props) =>
    props.widthContainerDesktop &&
    css`
      @media (min-width: 769px) {
        width: auto;
        flex: 1 1 ${reducedOnePercent(props.widthContainerDesktop)};
      }
    `}
`;

interface TitleProps {
  themeColor?: string;
}

export const Title = styled.span<TitleProps>`
  text-align: left;
  font-family: 'Unimed-Sans-Bold';
  font-size: 0.65rem;
  color: ${(props) => props.theme.colors.text.grayishToWhite};

  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(calc(-100% - 6px));

  ${(props) =>
    props.themeColor &&
    props.theme.title === 'light' &&
    css`
      color: ${props.themeColor};
    `}
  ${(props) =>
    props.theme.title === 'dark' &&
    css`
      color: ${props.theme.colors.text.grayishToWhite};
    `}
`;

export const Container = styled.div`
  position: relative;
  flex: 1;
  min-height: 30px !important;
`;

interface SelectedProps {
  isOpen: boolean;
  formLook?: boolean;
  isError?: boolean;
  isInputMode?: boolean;
  openUp?: boolean;
  bolder?: boolean;
}

const selectedStyle = css<SelectedProps>`
  background-color: #fff;
  position: relative;
  border: 1px solid ${(props) => props.theme.colors.text.secondary};
  box-sizing: border-box;
  border-radius: 10px;
  width: 100%;
  padding: 15px 20px;
  cursor: pointer;
  text-align: left;
  position: relative;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: 'Unimed-Sans-SemiBold';

  & > svg {
    color: ${(props) => props.theme.colors.text.primary};
    stroke-width: 1px;
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    transition: 300ms;

    ${(props) =>
      props.isError &&
      css`
        right: 40px;
      `}
  }

  ${(props) =>
    props.formLook &&
    css`
      font-family: 'Unimed-Sans-Book';
      padding: 8px 45px 8px 12px;
      border-radius: 5px;
      background: #fff;
      border-color: ${props.theme.colors.palet.institutional12};
      min-height: 36px;
      > svg {
        stroke-width: 1px;
      }
      &:disabled {
        background: ${props.theme.colors.palet.institutional14};
        border-color: ${props.theme.colors.palet.institutional14};
        cursor: default;
        > svg {
          display: none;
        }
      }
    `}

  ${(props) =>
    props.isOpen &&
    css`
      border-radius: 10px 10px 0 0;
      & > svg {
        transform: translateY(-50%) rotate(180deg);
      }
    `};

  ${(props) =>
    props.openUp &&
    props.isOpen &&
    css`
      border-radius: 0 0 10px 10px;
    `};

  ${(props) =>
    props.isError &&
    css`
      border-color: ${props.theme.colors.palet.institutional7};
    `}

  ${(props) =>
    props.bolder &&
    css`
      font-family: 'Unimed-Sans-Bold';
    `}
`;

export const Selected = styled.button`
  ${selectedStyle}
`;
export const SelectedInput = styled.input`
  ${selectedStyle}
  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    display: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
  }
  outline: none;
  &::placeholder {
    opacity: 0.5;
  }
`;

export const SelectedInputContainer = styled.div<SelectedProps>`
  position: relative;
  & > svg {
    color: ${(props) => props.theme.colors.text.primary};
    stroke-width: 1px;
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    transition: 300ms;

    ${(props) =>
      props.isError &&
      css`
        right: 40px;
      `}
  }
  ${(props) =>
    props.isOpen &&
    css`
      & > svg {
        transform: translateY(-50%) rotate(180deg);
      }
    `};
`;

interface OptionsWrapperProps {
  isOpen: boolean;
  calculateHeight: number;
  formLook?: boolean;
  openUp?: boolean;
}

export const OptionsWrapper = styled.div<OptionsWrapperProps>`
  button {
    cursor: pointer;
    padding: 15px 20px;
    width: 100%;
    color: ${(props) => props.theme.colors.text.primary};
    font-family: 'Unimed-Sans-SemiBold';
    background-color: ${(props) => props.theme.colors.text.white};
    border-bottom: none;
    text-align: left;
    border: 1px solid #c4cbcf;
    border-collapse: collapse;
    display: block;
    transition: 300ms;
    &:hover,
    &:focus {
      background-color: ${(props) => shade(0.1, props.theme.colors.text.white)};
    }
    ${(props) =>
      props.formLook &&
      css`
        font-family: 'Unimed-Sans-Book';
        padding: 8px 12px;
        background: #fff;
        border-color: ${props.theme.colors.palet.institutional12};
        border-top-width: 0;
        border-bottom-width: 0;
        &:hover {
          background-color: ${shade(0.1, '#fff')};
        }
      `}
  }

  button:last-child {
    border-radius: 0 0 10px 10px;
    border-bottom: 1px solid #c4cbcf;
  }
  button:first-child {
    border-radius: 0;
    border-top: 0;
  }
  position: absolute;
  min-width: 100%;
  overflow: hidden;
  height: 0;
  transition: height 250ms;
  max-height: 300px;
  border-collapse: collapse;
  z-index: 1;

  &[data-onhover='true'] {
    overflow-y: auto;
    height: auto;
  }

  ${(props) =>
    props.isOpen &&
    css`
      overflow-y: auto;
      height: ${props.calculateHeight}px;
    `};
  ${(props) =>
    props.openUp &&
    css`
      bottom: 100%;
      > button {
        &:first-child {
          border-radius: 10px 10px 0 0;
          border-top: 1px solid #c4cbcf;
        }
        &:last-child {
          border-radius: 0;
          border-bottom: 0;
        }
      }
    `}
`;

export const Error = styled.div`
  position: absolute;
  background-image: url("data:image/svg+xml,%3Csvg width='22' height='19' viewBox='0 0 22 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.36333 1.9188L1.25652 15.4525C1.08903 15.7396 1.00097 16.0746 1.00001 16.4C0.999051 16.735 1.08519 17.07 1.25077 17.3572C1.41635 17.6443 1.65563 17.8931 1.94372 18.0558C2.23181 18.2281 2.55915 18.3239 2.89319 18.3239H19.1068C19.4408 18.3239 19.7682 18.2281 20.0563 18.0558C20.3444 17.8931 20.5836 17.6443 20.7492 17.3572C20.9148 17.07 21.0009 16.735 21 16.4C20.999 16.0746 20.911 15.7396 20.7435 15.4525L12.6367 1.9188C12.4663 1.64123 12.2261 1.40199 11.9389 1.23928C11.6527 1.08614 11.3292 1 11 1C10.6708 1 10.3473 1.08614 10.0611 1.23928C9.77394 1.40199 9.5337 1.64123 9.36333 1.9188Z' stroke='%23ED1651' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M11 6.83853V10.667' stroke='%23ED1651' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M11 14.4955H11.01' stroke='%23ED1651' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-size: contain;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;

  span {
    visibility: hidden;
    opacity: 0;
    transform: translateY(20px);
    transition: 300ms !important;
    position: absolute;
    white-space: nowrap;
    right: -20px;
    top: -54px;
    background: ${(props) => props.theme.colors.palet.institutional7};
    color: ${(props) => props.theme.colors.text.inverse};
    font-family: 'Unimed-Sans-SemiBold';
    font-size: 1rem;
    padding: 6px 20px;
    border-radius: 10px;
    box-shadow: 0px 4px 4px rgba(255, 15, 100, 0.3);

    &::before {
      content: '';
      position: absolute;
      bottom: -10px;
      right: 20px;
      border: 5px solid ${(props) => props.theme.colors.palet.institutional7};
      border-right-color: transparent;
      border-bottom-color: transparent;
    }
  }

  &:hover {
    span {
      transition: 300ms;
      visibility: visible;
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
